import React from "react";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { logger } from "@/utils/logger";

/**
 * @typedef {Object} LeaderboardResponse
 * @property {string} track
 * @property {number} race
 * @property {string[]} firstFour
 * @property {Object} betOnValueBet
 */

/**
 * Fetches leaderboard data from the API
 * @param {Object} options - Optional parameters for the request
 * @param {string} options.date - Optional date filter in DD-MM-YYYY format. If not provided, fetches all data
 * @param {string} options.collection - Collection to fetch from ('predictions' or 'early-predictions')
 * @returns {Promise<LeaderboardResponse>}
 */
export const fetchLeaderboardData = async (options = {}) => {
  // logger.log("fetching leaderboard data");
  const url = getApiUrl("staible-returns-front-end");
  const { date, collection = "predictions" } = options;

  // logger.log("url", url);
  // logger.log("fetching data for date:", date || "all dates");

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      action: "get_model",
      model: "HayiAuto",
      subCollection: collection,
      filter: date || null, // If no date is provided, send null to get all data
    }),
  });
  return response.json();
};

/**
 * React Query hook for fetching and managing leaderboard data
 * @param {Object} options - Optional parameters for the request
 * @param {string} options.date - Optional date filter in DD-MM-YYYY format
 * @param {string} options.collection - Collection to fetch from ('predictions' or 'early-predictions')
 * @param {number} options.staleTime - Optional time in ms that data remains fresh (default: 5 minutes)
 * @param {number} options.cacheTime - Optional time in ms to keep unused data (default: 30 minutes)
 * @param {boolean} options.refetchOnWindowFocus - Whether to refetch when window regains focus
 * @param {boolean} options.refetchOnMount - Whether to refetch when component mounts
 */
export const useLeaderboardQuery = (options = {}) => {
  const {
    date,
    collection = "predictions",
    staleTime = 5 * 60 * 1000, // 5 minutes
    cacheTime = 30 * 60 * 1000, // 30 minutes
    refetchOnWindowFocus = false,
    refetchOnMount = false,
  } = options;

  return useQuery({
    queryKey: ["leaderboard", date, collection],
    queryFn: () => fetchLeaderboardData({ date, collection }),
    staleTime,
    cacheTime,
    refetchOnWindowFocus,
    refetchOnMount,
    refetchInterval: 60000, // 10 minutes
  });
};

/**
 * React Query hook for fetching early predictions
 * @param {Object} options - Optional parameters for the request
 * @param {string} options.date - Optional date filter in DD-MM-YYYY format
 * @param {number} options.staleTime - Optional time in ms that data remains fresh (default: 10 minutes)
 * @param {number} options.cacheTime - Optional time in ms to keep unused data (default: 30 minutes)
 * @param {boolean} options.refetchOnWindowFocus - Whether to refetch when window regains focus
 * @param {boolean} options.refetchOnMount - Whether to refetch when component mounts
 * @param {number} options.refetchInterval - Optional refetch interval in ms (default: 60000ms or 1 minute)
 */
export const useEarlyPredictionsQuery = (options = {}) => {
  const {
    date,
    staleTime = 10 * 60 * 1000, // 10 minutes
    cacheTime = 30 * 60 * 1000, // 30 minutes
    refetchOnWindowFocus = false,
    refetchOnMount = false,
    selectedTrack = null,
    selectedRace = null,
  } = options;

  return useQuery({
    queryKey: ["early-predictions", date],
    queryFn: () =>
      fetchLeaderboardData({ date, collection: "early-predictions" }),
    staleTime,
    cacheTime,
    refetchOnWindowFocus,
    refetchOnMount,
    refetchInterval: (data, query) => {
      // If no data yet, keep refetching every minute
      if (!data?.data) return 60000;

      // If no selected track or race, keep refetching
      if (!selectedTrack || !selectedRace) return 60000;

      // Get the race key for the current race
      const raceKey = `${selectedTrack.replace(/\s+/g, "-")}-${selectedRace}-${date}`;

      // Get early predictions data for this race
      const earlyData = data.data[raceKey];

      // If no early data, keep refetching
      if (!earlyData) return 60000;

      // Check if we have valid early predictions
      const hasFirstFourData =
        earlyData.firstFourData &&
        Array.isArray(earlyData.firstFourData) &&
        earlyData.firstFourData.length > 0;

      // If no firstFourData, keep refetching
      if (!hasFirstFourData) return 60000;

      // Additional check: if tipUpdatedAt exists and has a valid value
      // This follows the same logic as the hasEarlyPredictions check in the component
      const hasValidTipUpdatedAt = !!earlyData.tipUpdatedAt;

      // If we have early predictions with valid data, stop refetching
      return hasFirstFourData && hasValidTipUpdatedAt ? false : 60000;
    },
  });
};

/**
 * Fetches historical results data from the API
 * @param {Object} options - Options for the request
 * @param {string} options.date - Optional date to fetch results for in DD-MM-YYYY format
 * @param {boolean} options.fetchAll - Whether to fetch all historical data
 */
export const fetchHistoricalResults = async (options = {}) => {
  // logger.log("=== Historical Results Request Debug ===");
  // logger.log("Input options:", options);

  const url = getApiUrl("staible-returns-front-end");
  // logger.log("API URL:", url);

  const requestBody = {
    action: "read_document",
    model: "HayiAuto",
    subCollection: "results-archive",
  };

  // Only add date filter if fetchAll is false and a date is provided
  if (!options.fetchAll && options.date) {
    requestBody.filter = options.date;
    requestBody.filterField = "date";
  }

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(requestBody),
  });

  const responseData = await response.json();

  // Transform the array response into an object keyed by race ID
  if (responseData?.data?.data && Array.isArray(responseData.data.data)) {
    const transformedData = {};
    responseData.data.data.forEach((race) => {
      // Use the race ID as the key
      transformedData[race.id] = race;
    });

    return {
      status: "success",
      data: transformedData,
    };
  }

  return responseData;
};

/**
 * React Query hook for fetching historical results
 * @param {Object} options - Options for the query
 * @param {string} options.date - Optional date to fetch results for
 * @param {boolean} options.fetchAll - Whether to fetch all historical data
 * @param {number} options.staleTime - Optional time in ms that data remains fresh (default: 10 minutes)
 * @param {number} options.cacheTime - Optional time in ms to keep unused data (default: 60 minutes)
 * @param {boolean} options.refetchOnWindowFocus - Whether to refetch when window regains focus
 * @param {boolean} options.refetchOnMount - Whether to refetch when component mounts
 */
export const useHistoricalResultsQuery = (options = {}) => {
  const {
    date,
    fetchAll = false,
    staleTime = 10 * 60 * 1000, // 10 minutes
    cacheTime = 60 * 60 * 1000, // 60 minutes
    refetchOnWindowFocus = false,
    refetchOnMount = false,
  } = options;

  return useQuery({
    queryKey: ["historical-results", date, fetchAll],
    queryFn: () => fetchHistoricalResults({ date, fetchAll }),
    staleTime,
    cacheTime,
    refetchOnWindowFocus,
    refetchOnMount,
    refetchInterval: 60000, // 1 minute
    enabled: fetchAll || !!date, // Enable if fetchAll is true or if date is provided
  });
};

const getWorkspace = () => {
  // Use environment variable if available
  if (import.meta.env.VITE_APP_WORKSPACE) {
    // logger.log(
    //   `Using ${import.meta.env.VITE_APP_WORKSPACE} environment from env var`,
    // );
    return import.meta.env.VITE_APP_WORKSPACE;
  }

  // Fallback to hostname-based detection
  const hostname = window.location.hostname;

  if (hostname === "staging.lexi.tips") {
    logger.log("Using staging environment");
    return "staging";
  } else if (hostname === "lexi.tips") {
    logger.log("Using production environment");
    return "prod";
  }
  logger.log("Using development environment");
  return "dev";
};

export const getApiUrl = (functionName) => {
  const workspace = getWorkspace();
  // logger.log("API Workspace:", workspace);
  if (workspace === "dev" || workspace === "staging" || workspace === "prod") {
    return `https://lexi-${workspace}-front-end-mcdr62tc5a-ts.a.run.app`;
  } else {
    return `https://australia-southeast1-staible-returns.cloudfunctions.net/staible-returns-${workspace}-front-end`;
  }
};
